import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AuthProvider } from './context/AuthContext';
import { CssBaseline } from '@mui/material';

// ─── Import design tokens — change PRESET in theme.js to restyle the whole app
import { colors, typography as appTypography } from './theme';

const theme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: colors.bg,       // flat fallback
      paper:   colors.bgCard,
    },
    primary: {
      main:         colors.primary,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary,
    },
    text: {
      primary:   colors.textPrimary,
      secondary: colors.textMuted,
      disabled:  colors.textFaintest,
    },
    divider:  colors.border,
    error:    { main: colors.error },
    success:  { main: colors.success },
    warning:  { main: colors.warning },
    info:     { main: colors.info },
  },
  typography: {
fontFamily: `'Poppins', 'Playfair Display', 'Inter', 'Roboto', sans-serif`,
    h4:      { fontWeight: 700, color: colors.textPrimary },
    h5:      { fontWeight: 700, color: colors.textPrimary },
    h6:      { fontWeight: 600, color: colors.textPrimary },
    body1:   { color: colors.textPrimary },
    body2:   { color: colors.textMuted },
    caption: { color: colors.textFaintest },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // ── bgGradient gives the glass cards something to blur against ──────
          // Switch PRESET in theme.js and this updates automatically.
          background:           colors.bgGradient,
          backgroundAttachment: 'fixed',   // ← keeps gradient still while scrolling
          minHeight:            '100vh',
          color:                colors.textPrimary,
          scrollbarWidth: 'thin',
          scrollbarColor: `${colors.border} transparent`,
          '&::-webkit-scrollbar':       { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: colors.border, borderRadius: 3 },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.bgCard,
          border:          `1px solid ${colors.border}`,
          borderRadius:    12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.bgTopBar,
          borderBottom:    `1px solid ${colors.border}`,
          boxShadow:       'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: colors.primary,
          color:           '#ffffff',
          fontWeight:      700,
          textTransform:   'none',
          borderRadius:    8,
          '&:hover':    { backgroundColor: colors.primaryHover },
          '&:disabled': { backgroundColor: colors.border, color: colors.textFaintest },
        },
        outlinedPrimary: {
          borderColor:  colors.border,
          color:        colors.textPrimary,
          textTransform: 'none',
          borderRadius: 8,
          '&:hover': { borderColor: colors.primaryBorder, backgroundColor: colors.bgCard },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.bgCard,
            color:           colors.textPrimary,
            borderRadius:    8,
            '& fieldset':             { borderColor: colors.border },
            '&:hover fieldset':       { borderColor: colors.primaryBorder },
            '&.Mui-focused fieldset': { borderColor: colors.primary },
          },
          '& input::placeholder':              { color: colors.textFaintest, opacity: 1 },
          '& .MuiInputLabel-root':             { color: colors.textMuted },
          '& .MuiInputLabel-root.Mui-focused': { color: colors.primary },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: colors.border },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: colors.bgCard,
          border:          `1px solid ${colors.border}`,
          color:           colors.textPrimary,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.bgCard,
          border:          `1px solid ${colors.border}`,
          borderRadius:    8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color:     colors.textPrimary,
          fontSize:  14,
          '&:hover': { backgroundColor: colors.bgDeep },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: colors.bgCard },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.bgCard,
          border:          `1px solid ${colors.border}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',  // prevents MUI's default dark-mode overlay
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </ThemeProvider>
);

reportWebVitals();